/**
 * Stage 2 Write Hardening: Normalization utilities for deepsearch.items ingestion
 * 
 * STRICT RULES:
 * - meta.channel must be Title Case: Instagram, YouTube, TikTok, Web, Podcast, Reddit, LinkedIn, X
 * - meta.content_type must be one of: video, image, carousel, reel, link, audio, pdf, note
 * - source_table is ingestion lineage only: share_ios, swirl_it_modal, chrome_extension, web_clip, file_upload, api_import, migration_instagram_content
 */

/**
 * Normalize channel to strict Title Case
 * @param {string|null} value - Raw channel value (may be lowercase, mixed case, or alias)
 * @param {string|null} url - URL to derive channel from if value is missing
 * @returns {string} Title Case channel name
 */
export function normalizeChannel(value, url = null) {
  // Step 1: Normalize input to Title Case if present
  if (value && typeof value === 'string') {
    const valueLower = value.toLowerCase().trim();
    
    // Map known variants and aliases to canonical Title Case
    const channelMap = {
      'instagram': 'Instagram',
      'ig': 'Instagram',
      'youtube': 'YouTube',
      'yt': 'YouTube',
      'tiktok': 'TikTok',
      'tt': 'TikTok',
      'podcast': 'Podcast',
      'podcasts': 'Podcast',
      'spotify': 'Podcast',
      'apple podcasts': 'Podcast',
      'twitter': 'X',
      'x': 'X',
      'x.com': 'X',
      'linkedin': 'LinkedIn',
      'reddit': 'Reddit',
      'web': 'Web',
    };
    
    if (channelMap[valueLower]) {
      return channelMap[valueLower];
    }
    
    // If exact Title Case match from allowed list, return as-is
    const allowedChannels = ['Instagram', 'YouTube', 'TikTok', 'Web', 'Podcast', 'Reddit', 'LinkedIn', 'X'];
    if (allowedChannels.includes(value)) {
      return value;
    }
  }
  
  // Step 2: Derive from URL hostname if value missing
  if (url && typeof url === 'string') {
    const urlLower = url.toLowerCase();
    
    if (urlLower.includes('instagram.com')) return 'Instagram';
    if (urlLower.includes('youtube.com') || urlLower.includes('youtu.be')) return 'YouTube';
    if (urlLower.includes('tiktok.com')) return 'TikTok';
    if (urlLower.includes('open.spotify.com') || urlLower.includes('podcasts.apple.com')) return 'Podcast';
    if (urlLower.includes('x.com') || urlLower.includes('twitter.com')) return 'X';
    if (urlLower.includes('linkedin.com')) return 'LinkedIn';
    if (urlLower.includes('reddit.com')) return 'Reddit';
  }
  
  // Step 3: Default to Web
  return 'Web';
}

/**
 * Normalize content type to valid enum value
 * @param {string|null} value - Raw content_type value
 * @param {string|null} source_type - Fallback source_type from database
 * @returns {string} Valid content type
 */
export function normalizeContentType(value, source_type = null) {
  const validTypes = ['video', 'image', 'carousel', 'reel', 'link', 'audio', 'pdf', 'note'];
  
  // Step 1: If value exists and is valid, use it
  if (value && typeof value === 'string') {
    const valueLower = value.toLowerCase().trim();
    
    // Map common aliases
    const typeMap = {
      'post': 'image', // Instagram post → image
      'article': 'link',
      'url': 'link',
      'website': 'link',
      'document': 'pdf',
      'text': 'note',
      'photo': 'image',
      'picture': 'image',
    };
    
    const mapped = typeMap[valueLower] || valueLower;
    
    if (validTypes.includes(mapped)) {
      return mapped;
    }
  }
  
  // Step 2: Try source_type as fallback
  if (source_type && typeof source_type === 'string') {
    const sourceTypeLower = source_type.toLowerCase().trim();
    if (validTypes.includes(sourceTypeLower)) {
      return sourceTypeLower;
    }
  }
  
  // Step 3: Default to 'link'
  return 'link';
}

/**
 * Normalize source_table to canonical ingestion lineage value
 * 
 * CANONICAL VALUES (all underscores, no hyphens):
 * - share_ios: iOS share extension
 * - mobile_share: Mobile app share (separate from share_ios)
 * - swirl_it_modal: Web "Swirl It" button/modal
 * - chrome_extension: Chrome extension
 * - web_clip: Web clips
 * - file_upload: File uploads, drag & drop
 * - api_import: API-driven imports
 * - migration_instagram_content: Instagram legacy sync
 * 
 * @param {string} value - Raw source_table value
 * @returns {string} Canonical source_table value
 */
export function normalizeSourceTable(value) {
  if (!value || typeof value !== 'string') {
    return 'web_clip'; // Default for unknown sources
  }
  
  const valueLower = value.toLowerCase().trim();
  
  // Map variants to canonical values (ALL UNDERSCORES)
  const sourceTableMap = {
    // iOS share variants → share_ios
    'share-ios': 'share_ios', // Hyphen → underscore
    'ios_share': 'share_ios',
    'share_ios': 'share_ios',
    
    // Mobile share stays separate
    'mobile_share': 'mobile_share', // Keep as-is (NOT merged with share_ios)
    
    // Modal/button variants → swirl_it_modal
    'swirl_it': 'swirl_it_modal',
    'swirl_it_modal': 'swirl_it_modal',
    'modal': 'swirl_it_modal',
    
    // Extension variants → chrome_extension
    'chrome': 'chrome_extension',
    'extension': 'chrome_extension',
    'chrome_extension': 'chrome_extension',
    
    // Web clip variants → web_clip
    'web': 'web_clip',
    'web_clip': 'web_clip',
    'web_clips': 'web_clip', // Plural → singular
    
    // Upload variants → file_upload
    'upload': 'file_upload',
    'file': 'file_upload',
    'file_upload': 'file_upload',
    'manual': 'file_upload', // Legacy manual → file_upload
    
    // API variants → api_import
    'api': 'api_import',
    'api_import': 'api_import',
    
    // Migration variants → migration_instagram_content (legacy only)
    'instagram_content': 'migration_instagram_content',
    'migration_instagram': 'migration_instagram_content',
    'migration_instagram_content': 'migration_instagram_content',
  };
  
  return sourceTableMap[valueLower] || 'web_clip';
}

/**
 * Prepare complete item metadata for insertion
 * Ensures all required fields are present and normalized
 * 
 * @param {Object} itemData - Raw item data
 * @param {string} itemData.url - Item URL (required for channel derivation)
 * @param {string} itemData.source_type - Source type (required)
 * @param {Object} itemData.meta - Existing metadata object
 * @param {string} source_table - Ingestion source (will be normalized)
 * @returns {Object} Normalized item ready for insertion
 */
export function prepareItemForInsertion(itemData, source_table) {
  const meta = itemData.meta || {};
  
  // Enforce meta.channel (Title Case)
  const normalizedChannel = normalizeChannel(meta.channel, itemData.url);
  
  // Enforce meta.content_type
  const normalizedContentType = normalizeContentType(meta.content_type, itemData.source_type);
  
  // Normalize source_table
  const normalizedSourceTable = normalizeSourceTable(source_table);
  
  return {
    ...itemData,
    source_table: normalizedSourceTable,
    meta: {
      ...meta,
      channel: normalizedChannel, // Guaranteed Title Case
      content_type: normalizedContentType, // Guaranteed valid
    },
  };
}

